import React, { useEffect, useMemo, useRef, useState, useContext, Fragment } from 'react';

import moment from 'moment';

import { PacksContext, PacksDispatchContext } from '../../ApiProvider';
import { AssignContext, AssignDispatchContext } from '../../AssignProvider';

import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';

/**
 * Among selected calendar days (sorted ascending), return the first day where this pack is assigned on the venue (pivot.amount has that date).
 * Falls back to the first selected day if none match (e.g. data edge cases).
 */
function getFirstSelectedDateWherePackExists(packId, selectedVenue, sortedSelectedDayStrings) {
    if (!sortedSelectedDayStrings.length) {
        return null;
    }
    const relation = selectedVenue?.expense_packs?.find((p) => p.id == packId);
    const amountByDay = relation?.pivot?.amount;
    if (!amountByDay || typeof amountByDay !== 'object') {
        return sortedSelectedDayStrings[0];
    }
    const found = sortedSelectedDayStrings.find((day) => Object.prototype.hasOwnProperty.call(amountByDay, day));
    return found !== undefined ? found : sortedSelectedDayStrings[0];
}

function schedulesForFirstFilterDay(days, firstDay) {
    if (!Array.isArray(days) || !days.length) {
        return days;
    }
    const forDay = days.find((s) => s.date === firstDay);
    if (forDay) {
        return [forDay];
    }
    const sorted = [...days].sort((a, b) => moment(a.date).unix() - moment(b.date).unix());
    return sorted.length ? [sorted[0]] : days;
}

/** Same branching as EditContractorPackToVenueForm: saved instance list vs pivot expansion + legacy added_items. */
function resolveContractorCustomizationShape(dateCustomizations) {
    const raw = dateCustomizations?.items;
    if (Array.isArray(raw) && raw.length > 0) {
        return { mode: 'instances', itemsList: raw, legacyItemOverrides: {} };
    }
    const legacyItemOverrides = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    return { mode: 'expand', itemsList: null, legacyItemOverrides };
}

export default function VenueAssignedPack({ packId, amount }) {
    const { packs, contractorPackItems } = useContext(PacksContext);
    const [pack, setPack] = useState(false);
    const [total, setTotal] = useState(0);
    const apiCall = useContext(PacksDispatchContext);
    const { filteredPacks, venues, selectedVenue, filterDay, filterDays, formattedDate, formattedDates, selectedSchedules } = useContext(AssignContext);
    const assignAction = useContext(AssignDispatchContext);
    const assigned = filteredPacks.find((p) => p.packId == packId);
    const details = assigned && Object.prototype.hasOwnProperty.call(assigned, 'details') ? assigned.details : [];
    const allDetails = assigned && Object.prototype.hasOwnProperty.call(assigned, 'allDetails') ? assigned.allDetails : [];
    const allCustomizations = assigned && Object.prototype.hasOwnProperty.call(assigned, 'allCustomizations') ? assigned.allCustomizations : [];

    const thisPackDef = packs.find((p) => p.id == packId);
    const sortedDaysForContractorSlice = useMemo(
        () => [...filterDays].sort((a, b) => moment(a, 'YYYY-MM-DD').unix() - moment(b, 'YYYY-MM-DD').unix()),
        [filterDays],
    );
    /** Match EditContractorPackToVenueForm: one target day + per-day schedules/amounts when multiple days are selected. */
    const viewModalData = useMemo(() => {
        const isContractorMulti = thisPackDef?.pack_contractor && filterDays.length > 1;
        if (!isContractorMulti) {
            return {
                allDetails,
                allCustomizations,
                selectedSchedules,
                amountDataPerDetail: Array.isArray(assigned?.allAmountData) ? assigned.allAmountData : null,
            };
        }
        const targetDay = getFirstSelectedDateWherePackExists(packId, selectedVenue, sortedDaysForContractorSlice);
        const dayIndex = filterDays.findIndex((d) => d === targetDay);
        const sliceOk = dayIndex >= 0 && assigned?.allDetails?.[dayIndex] !== undefined;
        if (!sliceOk) {
            return {
                allDetails,
                allCustomizations,
                selectedSchedules,
                amountDataPerDetail: Array.isArray(assigned?.allAmountData) ? assigned.allAmountData : null,
            };
        }
        return {
            allDetails: [assigned.allDetails[dayIndex]],
            allCustomizations: assigned?.allCustomizations?.[dayIndex] !== undefined ? [assigned.allCustomizations[dayIndex]] : allCustomizations,
            selectedSchedules: schedulesForFirstFilterDay(selectedSchedules, targetDay),
            amountDataPerDetail:
                Array.isArray(assigned?.allAmountData) && assigned.allAmountData[dayIndex] !== undefined ? [assigned.allAmountData[dayIndex]] : null,
        };
    }, [
        thisPackDef?.pack_contractor,
        filterDays,
        sortedDaysForContractorSlice,
        packId,
        selectedVenue,
        allDetails,
        allCustomizations,
        selectedSchedules,
        assigned,
    ]);

    const detailsModal = useModal();
    const pendingShowModal = useRef(false);
    const pendingEditFormPayload = useRef(null);

    useEffect(() => {
        if (pendingShowModal.current) {
            pendingShowModal.current = false;
            detailsModal.show();
        }
    }, [filterDays]);

    useEffect(() => {
        if (pendingEditFormPayload.current) {
            const payload = pendingEditFormPayload.current;
            pendingEditFormPayload.current = null;
            apiCall('form', payload);
        }
    }, [filterDays]);

    const handleClick = (packId) => {
        const thisPack = packs.find((p) => p.id == packId);
        const isContractorPack = thisPack?.pack_contractor;
        const popupType = isContractorPack ? 'editPackToVenue' : 'packToVenue';
        const basePayload = {
            type: popupType,
            packId: packId,
            venueId: selectedVenue.id,
            filterDay: filterDay,
            filterDays: filterDays,
            formattedDate: formattedDate,
            formattedDates: formattedDates,
            selectedSchedules: selectedSchedules,
        };
        if (isContractorPack && filterDays.length > 1) {
            const sortedDays = [...filterDays].sort((a, b) => moment(a, 'YYYY-MM-DD').unix() - moment(b, 'YYYY-MM-DD').unix());
            const targetDay = getFirstSelectedDateWherePackExists(packId, selectedVenue, sortedDays);
            const targetFormatted = moment(targetDay, 'YYYY-MM-DD').format('DD MMMM YYYY');
            pendingEditFormPayload.current = {
                ...basePayload,
                filterDay: targetDay,
                filterDays: [targetDay],
                formattedDate: targetFormatted,
                formattedDates: [targetFormatted],
                selectedSchedules: schedulesForFirstFilterDay(selectedSchedules, targetDay),
            };
            assignAction('selectOnlyFirstDate', targetDay);
        } else {
            apiCall('form', basePayload);
        }
    };

    const handleViewClick = () => {
        const thisPack = packs.find((p) => p.id == packId);
        if (thisPack?.pack_contractor && filterDays.length > 1) {
            const sortedDays = [...filterDays].sort((a, b) => moment(a, 'YYYY-MM-DD').unix() - moment(b, 'YYYY-MM-DD').unix());
            const targetDay = getFirstSelectedDateWherePackExists(packId, selectedVenue, sortedDays);
            pendingShowModal.current = true;
            assignAction('selectOnlyFirstDate', targetDay);
        } else {
            detailsModal.show();
        }
    };

    useEffect(() => {
        setPack(packs.find((p) => p.id == packId));
    }, []);

    useEffect(() => {
        if (pack && pack.pack_children?.length) {
            let t = pack.pack_children.reduce((total, item) => total + item.pivot.amount * item.price, 0);
            t = t * amount;
            setTotal(t);
        }
    }, [amount, pack]);

    return (
        <div>
            <div className="tw-rounded-lg tw-border-2 tw-border-gray-200">
                <div className="tw-p-2.5 tw-space-y-4">
                    <div className="tw-flex tw-gap-4">
                        <div className="tw-basis-full">
                            {pack.name} {!pack.pack_contractor && `(${amount})`}
                        </div>

                        <div className="tw-flex tw-items-center tw-justify-end tw-space-x-3">
                            <button onClick={() => handleClick(packId)}>
                                <EditIcon className="tw-w-4 tw-h-4" />
                            </button>
                            <button onClick={handleViewClick}>
                                <EyeIcon className="tw-w-4 tw-h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <VenueAssignedPackModal
                id="VENUE_ASSIGNED_PACK_MODAL"
                modal={detailsModal}
                pack={pack}
                amount={amount}
                details={details}
                allDetails={viewModalData.allDetails}
                selectedSchedules={viewModalData.selectedSchedules}
                allCustomizations={viewModalData.allCustomizations}
                amountDataPerDetail={viewModalData.amountDataPerDetail}
                contractorPackItems={contractorPackItems}
                total={total}
            />
        </div>
    );
}

const VenueAssignedPackModal = (props) => {
    const selectedPack = useContext(AssignContext).selectedPack;

    const close = () => {
        props.modal.hide();
    };

    return (
        <Modal id={props.id} modal={props.modal} containerStyle={{ maxWidth: '1280px' }}>
            <ModalHeader title={props.pack.pack_contractor ? props.pack.name : `${props.pack.name} (${props.amount})`} close={close}></ModalHeader>
            <div className="tw-rounded-b">
                <div className="modal__form-container">
                    <div className={(selectedPack && selectedPack.id === props.pack.id ? 'venue-assigned-selected-pack' : '') + ' venue-assigned-pack'}>
                        {props.pack.pack_children && props.pack.pack_children.length ? (
                            <PackDetails
                                pack={props.pack}
                                details={props.details}
                                allDetails={props.allDetails}
                                selectedSchedules={props.selectedSchedules}
                                amount={props.amount}
                                amountDataPerDetail={props.amountDataPerDetail}
                                total={props.total}
                                allCustomizations={props.allCustomizations}
                                contractorPackItems={props.contractorPackItems}
                            />
                        ) : (
                            <NoItemsInPack />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const PackDetails = (props) => {
    const packQtyForDetailIndex = (detailIndex) => {
        if (Array.isArray(props.amountDataPerDetail) && props.amountDataPerDetail[detailIndex] !== undefined) {
            return props.amountDataPerDetail[detailIndex];
        }
        return props.amount || 1;
    };

    const scheduleForDetailIndex = (detailIndex) => {
        const s = props.selectedSchedules;
        if (!s) {
            return null;
        }
        if (Array.isArray(s) && s.length > 0) {
            return s[detailIndex] ?? s[0];
        }
        return s;
    };

    const [totalData, setTotalData] = useState(
        props.pack.pack_contractor
            ? {
                  name: 'Total',
                  total: 0,
                  qty1: 0,
                  qty2: 0,
                  qty3: 0,
                  people: 0,
              }
            : {
                  name: 'Total',
                  total: props.total,
              },
    );

    const calculateItemDetails = (item, details, detailIndex = 0) => {
        let total = 0;
        let array = [
            {
                name: 'Quantity shift 1',
                hours: 0,
                total: 0,
            },
        ];

        const sched = scheduleForDetailIndex(detailIndex);
        if (sched && item.pack_contractor) {
            let duration = details && 'duration' in details ? details['duration'] : timeToFloat(sched.duration);

            const useSessionTime = 'useSessionTime' in details ? details.useSessionTime : true;
            duration += useSessionTime ? item.start_time_before + item.finish_time_after : 0;

            if (duration > item.max_shift_duration) {
                array = findShifts(duration, item.price, item.max_shift_duration, item.min_shift_duration, item.shift_overlap);
                for (let i = 0; i < array.length; i++) {
                    total += array[i]['total'];
                }
            } else {
                if (duration <= item.max_shift_duration && duration >= item.min_shift_duration) {
                    total = duration.toFixed(2) * item.price;
                } else if (duration < item.min_shift_duration) {
                    total = item.price * item.min_shift_duration;
                }

                array[0].hours = duration;
                array[0].total = total;
            }
        }

        return { array, total };
    };

    const buildViewRowModel = (item, calculated, customization) => {
        const ratePerHour =
            customization && customization.rate_per_hour !== undefined && customization.rate_per_hour !== null
                ? parseFloat(customization.rate_per_hour) || 0
                : item.price;

        const baseArray = calculated && Array.isArray(calculated.array) ? calculated.array : [];

        const getShiftHours = (shiftIndex) => {
            const baseHours = baseArray[shiftIndex]?.hours || 0;
            const idx = shiftIndex + 1;
            const shiftOverride = customization && customization.shifts && customization.shifts[idx];

            if (shiftOverride && shiftOverride.start && shiftOverride.end && shiftOverride.start !== '-' && shiftOverride.end !== '-') {
                return Math.max(0, timeToFloat(shiftOverride.end) - timeToFloat(shiftOverride.start));
            }

            return baseHours;
        };

        const qty1 = getShiftHours(0);
        const qty2 = getShiftHours(1);
        const qty3 = getShiftHours(2);
        const totalHours = qty1 + qty2 + qty3;
        const rowTotal = ratePerHour * totalHours;
        const people = [qty1, qty2, qty3].filter((h) => h > 0).length;

        return { ratePerHour, qty1, qty2, qty3, rowTotal, people };
    };

    useEffect(() => {
        if (!props.pack.pack_contractor) {
            return;
        }

        const dataTotal = {
            name: 'Total',
            total: 0,
            qty1: 0,
            qty2: 0,
            qty3: 0,
            people: 0,
        };

        props.allDetails.forEach((det, detailIndex) => {
            const dateCustomizations = props.allCustomizations?.[detailIndex] || {};
            const { mode, itemsList, legacyItemOverrides } = resolveContractorCustomizationShape(dateCustomizations);
            const addedItemCustomizations = Array.isArray(dateCustomizations.added_items) ? dateCustomizations.added_items : [];

            if (mode === 'instances' && itemsList) {
                itemsList.forEach((entry) => {
                    const item = props.contractorPackItems?.find((packItem) => packItem.id == entry.item_id);
                    if (!item) {
                        return;
                    }
                    const calculated = calculateItemDetails(item, det, detailIndex);
                    const customization = {
                        rate_per_hour: entry.rate_per_hour,
                        shifts: entry.shifts,
                    };
                    const model = buildViewRowModel(item, calculated, customization);
                    dataTotal.total += model.rowTotal;
                    dataTotal.qty1 += model.qty1;
                    dataTotal.qty2 += model.qty2;
                    dataTotal.qty3 += model.qty3;
                    dataTotal.people += model.people;
                });
                return;
            }

            const qtyForDay = packQtyForDetailIndex(detailIndex);
            props.pack.pack_children.forEach((p) => {
                const calculated = calculateItemDetails(p, det, detailIndex);
                const customization = legacyItemOverrides[p.id];
                const model = buildViewRowModel(p, calculated, customization);
                const repeat = qtyForDay * (p.pivot.amount || 1);

                dataTotal.total += model.rowTotal * repeat;
                dataTotal.qty1 += model.qty1 * repeat;
                dataTotal.qty2 += model.qty2 * repeat;
                dataTotal.qty3 += model.qty3 * repeat;
                dataTotal.people += model.people * repeat;
            });

            addedItemCustomizations.forEach((addedItem) => {
                const item = props.contractorPackItems?.find((packItem) => packItem.id == addedItem.item_id);
                if (!item) {
                    return;
                }

                const calculated = calculateItemDetails(item, det, detailIndex);
                const model = buildViewRowModel(item, calculated, addedItem);

                dataTotal.total += model.rowTotal;
                dataTotal.qty1 += model.qty1;
                dataTotal.qty2 += model.qty2;
                dataTotal.qty3 += model.qty3;
                dataTotal.people += model.people;
            });
        });

        setTotalData(dataTotal);
    }, [props.pack, props.allDetails, props.allCustomizations, props.selectedSchedules, props.contractorPackItems, props.amount, props.amountDataPerDetail]);

    return (
        <div className="tw-space-y-2">
            <VenueAssignedItem pack={props.pack.pack_contractor} calculated={{ total: 0, array: [] }} />
            {props.pack.pack_contractor &&
                props.allDetails.map((det, detailIndex) => {
                    const dateCustomizations = props.allCustomizations?.[detailIndex] || {};
                    const { mode, itemsList, legacyItemOverrides } = resolveContractorCustomizationShape(dateCustomizations);
                    const addedItemCustomizations = Array.isArray(dateCustomizations.added_items) ? dateCustomizations.added_items : [];

                    if (mode === 'instances' && itemsList) {
                        return (
                            <Fragment key={detailIndex}>
                                {itemsList.map((entry, entryIndex) => {
                                    const item = props.contractorPackItems?.find((packItem) => packItem.id == entry.item_id);
                                    if (!item) {
                                        return null;
                                    }
                                    const calculated = calculateItemDetails(item, det, detailIndex);
                                    const customization = {
                                        rate_per_hour: entry.rate_per_hour,
                                        shifts: entry.shifts,
                                    };
                                    const model = buildViewRowModel(item, calculated, customization);
                                    const rowKey = entry.instance_id != null ? String(entry.instance_id) : `entry-${detailIndex}-${entryIndex}-${item.id}`;
                                    return (
                                        <ContractorVenueAssignedRow
                                            key={rowKey}
                                            item={item}
                                            ratePerHour={model.ratePerHour}
                                            qty1={model.qty1}
                                            qty2={model.qty2}
                                            qty3={model.qty3}
                                            people={model.people}
                                            rowTotal={model.rowTotal}
                                            repeatCount={1}
                                            isCustom={entry.is_original === false}
                                        />
                                    );
                                })}
                            </Fragment>
                        );
                    }

                    return (
                        <Fragment key={detailIndex}>
                            {props.pack.pack_children.map((p) => {
                                const calculated = calculateItemDetails(p, det, detailIndex);
                                const customization = legacyItemOverrides[p.id];
                                const model = buildViewRowModel(p, calculated, customization);
                                const repeatCount = (p.pivot?.amount || 1) * packQtyForDetailIndex(detailIndex);

                                return (
                                    <ContractorVenueAssignedRow
                                        key={`base-${detailIndex}-${p.id}`}
                                        item={p}
                                        ratePerHour={model.ratePerHour}
                                        qty1={model.qty1}
                                        qty2={model.qty2}
                                        qty3={model.qty3}
                                        people={model.people}
                                        rowTotal={model.rowTotal}
                                        repeatCount={repeatCount}
                                    />
                                );
                            })}
                            {addedItemCustomizations.map((addedItem, addedIndex) => {
                                const item = props.contractorPackItems?.find((packItem) => packItem.id == addedItem.item_id);
                                if (!item) {
                                    return null;
                                }

                                const calculated = calculateItemDetails(item, det, detailIndex);
                                const model = buildViewRowModel(item, calculated, addedItem);

                                return (
                                    <ContractorVenueAssignedRow
                                        key={`added-${detailIndex}-${addedIndex}-${item.id}`}
                                        item={item}
                                        ratePerHour={model.ratePerHour}
                                        qty1={model.qty1}
                                        qty2={model.qty2}
                                        qty3={model.qty3}
                                        people={model.people}
                                        rowTotal={model.rowTotal}
                                        repeatCount={1}
                                        isCustom
                                    />
                                );
                            })}
                        </Fragment>
                    );
                })}
            {!props.pack.pack_contractor &&
                props.allDetails.map((det, detailIndex) => {
                    return props.pack.pack_children.map((p) => {
                        const calculated = calculateItemDetails(p, det, detailIndex);

                        return (
                            <VenueAssignedItem calculated={calculated} item={p} amount={packQtyForDetailIndex(detailIndex)} key={`${detailIndex}-${p.id}`} />
                        );
                    });
                })}
            <TotalsRow contractor={props.pack.pack_contractor} total={props.total} totalData={totalData} />
        </div>
    );
};

const findShifts = (duration, price, max_shift_duration, min_shift_duration, shift_overlap, shifts = 0, array = []) => {
    if (max_shift_duration === 0) {
        return array;
    }

    shifts++;
    duration = shifts > 1 ? duration + shift_overlap : duration;
    let hours = 0;
    let total = 0;

    if (duration >= max_shift_duration) {
        hours = max_shift_duration;
    } else if (duration >= min_shift_duration) {
        hours = duration;
    } else {
        hours = min_shift_duration;
    }

    total += price * hours;
    array.push({ name: `Quantity shift ${shifts}`, hours: hours, total: total });
    duration -= hours;

    if (duration > 0) {
        findShifts(duration, price, max_shift_duration, min_shift_duration, shift_overlap, shifts, array);
    }

    return array;
};

const VenueAssignedItem = ({ calculated, item, amount, pack }) => {
    let total = calculated.total;
    let array = calculated.array;

    if (item === undefined) {
        return pack ? (
            <div className="tw-grid tw-grid-cols-10 tw-gap-4 tw-text-sm tw-font-semibold tw-text-center">
                <div className="tw-col-span-4 tw-text-left">{$t('Description')}</div>
                <div>{$t('Rate per hour')}</div>
                <div>{$t('Qty Shift 1')}</div>
                <div>{$t('Qty Shift 2')}</div>
                <div>{$t('Qty Shift 3')}</div>
                <div>{$t('People')}</div>
                <div>{$t('Total')}</div>
            </div>
        ) : (
            <div className="tw-grid tw-grid-cols-9 tw-gap-4 tw-text-sm tw-font-semibold tw-text-center">
                <div className="tw-col-span-4 tw-text-left">{$t('Name')}</div>
                <div>{$t('Rate')}</div>
                <div>{$t('Qty per pack')}</div>
                <div>{$t('Qty')}</div>
                <div>{$t('Total Qty')}</div>
                <div>{$t('Total')}</div>
            </div>
        );
    }

    return item.pack_contractor ? (
        _.range(item.pivot.amount).map((i) => (
            <div className="tw-grid tw-grid-cols-10 tw-gap-4 tw-text-sm" key={i}>
                <div className="tw-col-span-4">
                    {i} {item.pivot.amount} {item ? item.name : ''}
                </div>
                <div className="tw-text-right inbud-numeric-cell">{item ? item.price : ''}</div>
                {array.length > 0 &&
                    array.map((e, idx) => (
                        <div key={idx} className="tw-text-right inbud-numeric-cell">
                            {e.hours.toFixed(1)}
                        </div>
                    ))}
                {array.length === 1 && (
                    <>
                        <div className="tw-text-right">-</div>
                        <div className="tw-text-right">-</div>
                    </>
                )}
                {array.length === 2 && <div className="tw-text-right">-</div>}
                <div className="tw-text-right inbud-numeric-cell">{array.length}</div>
                <div className="tw-text-right inbud-numeric-cell">{total.toFixed(1)}</div>
            </div>
        ))
    ) : (
        <div className="tw-grid tw-grid-cols-9 tw-gap-4 tw-text-sm">
            <div className="tw-col-span-4">{item.name}</div>
            <div className="tw-text-right inbud-numeric-cell">{item.price}</div>
            <div className="tw-text-right inbud-numeric-cell">{item.pivot.amount}</div>
            <div className="tw-text-right inbud-numeric-cell">{amount}</div>
            <div className="tw-text-right inbud-numeric-cell">{item.pivot.amount * amount}</div>
            <div className="tw-text-right inbud-numeric-cell">{item.pivot.amount * amount * item.price}</div>
        </div>
    );
};

const ContractorVenueAssignedRow = ({ item, ratePerHour, qty1, qty2, qty3, people, rowTotal, repeatCount = 1, isCustom = false }) => {
    return _.range(repeatCount).map((i) => (
        <div className="tw-grid tw-grid-cols-10 tw-gap-4 tw-text-sm" key={i}>
            <div className="tw-col-span-4">
                {item ? item.name : ''}
                {isCustom && <span className="tw-ml-1 tw-text-xs tw-text-gray-500 tw-border tw-rounded tw-px-1">{$t('Custom')}</span>}
            </div>
            <div className="tw-text-right inbud-numeric-cell">{ratePerHour}</div>
            <div className="tw-text-right inbud-numeric-cell">{qty1 ? qty1.toFixed(1) : '-'}</div>
            <div className="tw-text-right inbud-numeric-cell">{qty2 ? qty2.toFixed(1) : '-'}</div>
            <div className="tw-text-right inbud-numeric-cell">{qty3 ? qty3.toFixed(1) : '-'}</div>
            <div className="tw-text-right inbud-numeric-cell">{people || people === 0 ? people : '-'}</div>
            <div className="tw-text-right inbud-numeric-cell">{rowTotal ? rowTotal.toFixed(1) : '-'}</div>
        </div>
    ));
};

const TotalsRow = (props) => {
    return (
        <>
            {props.contractor === 0 && (
                <div className="tw-grid tw-grid-cols-9 tw-gap-4 tw-mt-4 tw-text-sm tw-font-semibold">
                    <div className="tw-col-span-8">{props.totalData.name}</div>
                    <div className="tw-col-span-1 inbud-numeric-cell tw-text-right">{props.total}</div>
                </div>
            )}
            {props.contractor === 1 && (
                <div className="tw-grid tw-grid-cols-10 tw-gap-4 tw-mt-4 tw-text-sm tw-font-semibold">
                    <div className="tw-col-span-4">{props.totalData.name}</div>
                    <div>{/* empty cell */}</div>
                    <div className="inbud-numeric-cell tw-text-right">{Math.round(props.totalData.qty1)}</div>
                    <div className="inbud-numeric-cell tw-text-right">{props.totalData.qty2 ? Math.round(props.totalData.qty2) : '-'}</div>
                    <div className="inbud-numeric-cell tw-text-right">{props.totalData.qty3 ? Math.round(props.totalData.qty3) : '-'}</div>
                    <div className="inbud-numeric-cell tw-text-right">{props.totalData.people}</div>
                    <div className="inbud-numeric-cell tw-text-right">{Math.round(props.totalData.total)}</div>
                </div>
            )}
        </>
    );
};

const NoItemsInPack = () => {
    return <p className="tw-text-sm tw-text-charcoal-50">{$t('No items in this pack')}</p>;
};

const timeToFloat = (time) => {
    const hoursMinutes = time.toString().split(':');
    const hours = parseInt(hoursMinutes[0], 10);
    const minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;
    return hours + minutes / 60;
};
